/**
 * `AW31` — **la primitiva de paginado.** UNA definición para los OCHO sitios de
 * render que había en siete archivos.
 *
 * ⚠️⚠️ `R3-UI0` `[25/9/2026]` · **ESE «OCHO» CUENTA LAS COPIAS QUE SE FUSIONARON EN
 * SU MOMENTO, NO LOS CONSUMIDORES DE HOY, y confundirlos hace estimar mal un
 * radio.** Los consumidores de hoy, **medidos** con
 * `grep -rn "import { Paginado" apps/management-ui/src` y
 * `grep -rn "<Paginado" apps/management-ui/src`:
 *
 *   · **10 archivos** importan `Paginado` — `banco-de-contenido/tabla.tsx`,
 *     `configuracion-del-chatbot/auditoria.tsx`, `conversaciones/tabla.tsx`,
 *     `envios/formulario.tsx`, `envios/pantalla.tsx`, `exportacion/pantalla.tsx`,
 *     `metricas/pantalla.tsx`, `padron/tabla.tsx`,
 *     `plantillas/botones/pantalla.tsx`, `plantillas/pantalla.tsx`;
 *   · **11 sitios de render** (`metricas/pantalla.tsx` monta DOS barras, `:867` y
 *     `:944`; la coincidencia 12 de ese `grep` es un comentario en
 *     `envios/formulario.tsx:1152`);
 *   · de esos 11, **2 son de R3** (`conversaciones/tabla.tsx:293`,
 *     `padron/tabla.tsx:1377`) y **9 no**.
 *
 * ⇒ cada vez que este archivo dice cuántas pantallas toca un cambio, el número es
 * **9 pre-R3 / 11 sitios en 10 archivos**, y va con el comando al lado para que el
 * próximo lo re-mida en vez de heredarlo. La dirección de todo argumento de abajo
 * **no se invierte**: el radio es mayor, no menor.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ POR QUÉ ESTE ARCHIVO EXISTE, Y POR QUÉ ES UN COMPONENTE Y NO UNA REGLA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Medido el 28/8/2026, antes de tocar nada: la barra de paginado estaba copiada
 * a mano en ocho lugares —`banco-de-contenido/tabla.tsx`, `conversaciones/tabla.tsx`,
 * `padron/tabla.tsx`, `metricas/pantalla.tsx` (dos veces), `envios/pantalla.tsx`,
 * `envios/formulario.tsx` y `exportacion/pantalla.tsx`— y `estilos.css` las
 * alcanzaba con una **lista de seis selectores escrita a mano**.
 *
 * ⛔ **El octavo ya estaba afuera, y nada lo decía.** `envios/formulario.tsx` no
 * llevaba la clase `__paginado`, así que **no recibía ninguna de las cuatro reglas**
 * del bloque de paginado: se veía distinto de los otros siete y ninguna corrida lo
 * informaba. Ésa es la prueba de que copiar no escala, no una teoría sobre ello.
 *
 * ⭐ Es la TERCERA ocurrencia de la misma especie en `estilos.css`, y las dos
 * anteriores dejaron su rastro ahí: el título de pantalla (dejaba tres afuera, se
 * resolvió des-enumerando a `main h1`) y la lista de paneles (dejó afuera a
 * `.exportacion__selector`, y declara que **no** se puede des-enumerar). La lección
 * escrita es: *si el rasgo se puede decir en un selector, se dice*. Acá se puede —y
 * mejor todavía—: **el rasgo se dice en un componente**, y el CSS pasa de seis
 * selectores a uno (`.paginado`). Una pantalla nueva no puede quedar afuera porque
 * no hay lista a la que olvidarse de agregarla.
 *
 * ⭐ La mitad del trabajo ya estaba hecha y encerrada: `metricas/pantalla.tsx`
 * tenía un `function Paginado(props)` **sin exportar**. Esto es ese componente,
 * sacado a módulo propio y con el campo escribible agregado UNA vez y no ocho.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL NÚMERO DE PÁGINAS LO CALCULA LA PRIMITIVA, A PARTIR DE `total`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Las seis pantallas que paginan contra un borde declaran el mismo `guard`: el
 * número de páginas sale de `total` —el total EN LA BASE— y **nunca** de
 * `items.length`. Antes cada una lo calculaba por su cuenta y el `guard` dependía
 * de que las seis lo hicieran bien.
 *
 * ⇒ esta primitiva recibe `total` y `tamanoDePagina` y hace la cuenta ella. No
 * recibe un `paginas` ya calculado a propósito: **un parámetro que se puede pasar
 * mal es un `guard` que se puede romper en silencio.** Si una pantalla le pasara
 * `items.length` como `total` estaría mintiendo de forma visible en su propia
 * llamada, que es donde se lee.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL CAMPO ESCRIBIBLE: LO QUE SE ESCRIBE SE VALIDA, Y FUERA DE RANGO NO NAVEGA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `AW31` agrega que el operador pueda ESCRIBIR el número de página. Las tres
 * reglas, en orden de importancia:
 *
 *  ① **Un valor fuera de rango no navega.** Ni salta a la última página, ni se
 *    queda callado: **dice qué pasó**, en un `role="alert"`, y el valor escrito
 *    **se queda en el campo** para que se pueda corregir. Un paginador que corrige
 *    solo lo que el operador escribió le enseña a no mirar — y la próxima vez que
 *    se equivoque de verdad tampoco va a mirar.
 *  ② **Es un `<form>`, no un `onKeyDown`.** Enter envía porque es un formulario, y
 *    hay un botón «Ir» visible: el que navega con teclado y el que navega con el
 *    pulgar llegan al mismo lugar sin que la pantalla tenga que adivinar cuál es.
 *  ③ **El campo sólo aparece si hay a dónde ir** (`paginas > 1`) **en la barra
 *    PRE-R3**; en la barra de R3 está siempre. Con una sola página, ofrecer «Ir a
 *    la página» es ofrecer una navegación que no existe — y eso sigue siendo
 *    cierto para los ~~seis~~ **nueve** sitios de render que no son de R3 (ver el
 *    encabezado de este archivo: medido, 11 sitios en 10 archivos, 2 de R3).
 *
 *    ════════════════════════════════════════════════════════════════════════
 *    ⛔⛔⛔ `R3-UI0` `[25/9/2026]` · **ACÁ CHOCAN DOS AUTORIDADES VIVAS, Y LA
 *    RECONCILIACIÓN VA ESCRITA PORQUE NO ES OBVIA — ES UNA `⛔ PARADA`
 *    REPORTADA, CON DUEÑO.**
 *    ════════════════════════════════════════════════════════════════════════
 *
 *    **Autoridad A · `PRD_R3.md:682`** lista los elementos del paginado —*«“Filas
 *    por página”, “Anterior”, “Página N de M · T mensajes”, “Siguiente” e “Ir a
 *    la página”»*— **sin condición**, y `:721`/`:723` describen la misma barra con
 *    el conteo en *«Página 1 de 1 · 0 mensajes»*, que es el caso de una sola
 *    página con cero filas. ⇒ la barra de R3 tiene los CINCO elementos siempre.
 *
 *    **Autoridad B · dos prohibiciones que hoy están VERDES**, y las dos son de
 *    pantallas que **ningún criterio de R3 toca**:
 *      · `R1-F3-28` (`PRD_R1.md:1323`, *«No hay búsqueda ni filtro en la tabla ·
 *        **Confirmado como decisión, no como faltante**»*), que
 *        `pruebas/aw47-declaracion-del-banco.test.tsx` §5.12 hace cumplir sobre
 *        el **Banco de contenido** con `querySelectorAll("input, textarea,
 *        select")` ⇒ `[]`, y cuyo propio mensaje dice: *«§5.12 es una prohibición
 *        activa: si el banco real se vuelve incómodo, **es alcance nuevo y hay
 *        que pedirlo**»*.
 *      · `CA-88` (`pruebas/aw48-configuracion-del-chatbot.test.tsx:1587`), sobre
 *        la pestaña **Auditoría**: *«no hay NINGÚN otro control en la pestaña …
 *        los únicos botones son los dos del paginado compartido»* ⇒
 *        `["Anterior", "Siguiente"]`.
 *
 *    ⭐ **MEDIDO `[25/9/2026]`, no deducido:** sacar la guarda a secas pone esos
 *    **dos** casos en rojo —y sólo esos dos: se revirtió este archivo solo y los
 *    diez rojos restantes de la corrida siguieron rojos, o sea que son de otro
 *    frente—. El Banco recibe el `<input inputmode="numeric">` de esta barra y la
 *    Auditoría recibe un tercer botón, «Ir».
 *
 *    ⇒ **la reconciliación: el quinto elemento es de la BARRA DE R3.** Cuando la
 *    pantalla pide la barra de R3 —lo que hace pasando `onTamanoDePagina`, que es
 *    el otro elemento que R3 agrega a la lista— «Ir a la página» está **siempre**,
 *    también con una página y con `total` 0. Cuando no la pide, la barra es
 *    **exactamente la de antes**.
 *
 *    ⭐ Y no es un parche: `PRD_R3.md:682` y `:721` describen el paginado **de
 *    Conversaciones**, y `:1996` el de Participantes. **Ninguno legisla el Banco
 *    de contenido ni la pestaña Auditoría**, que son de `R1-F3` y de `R2-UI7`. La
 *    lista de cinco elementos vale donde el PRD que la escribe tiene autoridad, y
 *    las dos pantallas de R3 la reciben entera.
 *
 *    ⛔⛔ **ENMIENDA `[25/9/2026]` · ACÁ DECÍA UNA COSA FALSA, Y ERA JUSTO LA QUE
 *    DECIDE EL PRÓXIMO CHOQUE.** Decía: ~~*«Las dos pantallas que piden la barra
 *    de R3 son, además, **las dos únicas que piden “Filas por página”**, así que
 *    el opt-in no inventa un eje»*~~.
 *
 *    **Medido `[25/9/2026]`, `grep -rn "Filas por página" apps/management-ui/src`:
 *    hay DOS pantallas más que lo dibujan hoy**, por `children` y con escala
 *    propia — `configuracion-del-chatbot/auditoria.tsx:450` (Auditoría) y
 *    `envios/formulario.tsx:786` (Nuevo envío). Son **cuatro**, no dos.
 *
 *    ⇒ **el eje real es «pide “Filas por página” POR EL PROP», no «pide “Filas
 *    por página”».** Y esa distinción era invisible en la firma: se derivaba de
 *    `onTamanoDePagina !== undefined` y sólo vivía en este párrafo. El día que
 *    alguien migre esas dos copias —y el `acceptance` de `R3-UI0` dice que
 *    *«migrarlas es otra etapa»*, o sea que **está planificado**— heredaban «Ir a
 *    la página» **en silencio**, y `R1-F3-28` y `CA-88` volvían a rojo con un
 *    comentario al lado que decía que no podía pasar.
 *
 *    ⭐ **Por eso el eje ahora es un prop propio, `conIrALaPagina`** (ver su
 *    docblock abajo): la derivación sigue siendo el default —la decisión del
 *    operador no cambia—, pero la migración tiene una salida declarada
 *    (`conIrALaPagina={false}`) en vez de un efecto colateral, y `aw31` la mide en
 *    los tres estados. **Un invariante que sólo vive en un comentario no lo hace
 *    cumplir nadie.**
 *
 *    ⛔⛔ **LO QUE ESTO NO RESUELVE, Y ES LA `PARADA` QUE VA AL INFORME.** Si lo
 *    que se quiere es que la barra tenga los cinco elementos en ~~las ocho~~
 *    **los once sitios de render** (en 10 archivos, medido), hay que enmendar
 *    `aw47` §5.12 y `aw48` `CA-88`, y eso es
 *    **alcance nuevo por escrito** (el mensaje de `§5.12` lo dice con esas
 *    palabras) sobre criterios de R1 y R2 que hoy están verdes. **No es una
 *    decisión de esta etapa y no se tomó acá.** El cambio es de UNA línea en la
 *    condición de abajo, en cualquiera de los dos sentidos. **Dueño: el operador
 *    / TL.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐ `R3-UI0` · «FILAS POR PÁGINA» ES PARTE DE LA PRIMITIVA, Y ES OPT-IN
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:721` y la regla 27 (`:880`) piden el selector con **10, 50, 100, 200
 * y 500, y 10 al abrir**, en Conversaciones (R3-F1) y en Participantes (R3-F5).
 *
 * ⛔⛔ **NO se vuelve obligatorio, y ésa es la decisión.** Hoy el selector lo
 * pasan por `children` **dos** pantallas, con escalas propias que ningún criterio
 * de R3 cambia: `configuracion-del-chatbot/auditoria.tsx:233` con `[25, 50, 100,
 * 200]` —*«una escalera corta … de una bitácora que se lee de a poco»*— y
 * `envios/formulario.tsx:132` con `[50 … 3000]`, que es una escalera de padrón.
 * Volverlo obligatorio le cambiaría la escala a ~~las ocho pantallas~~ **los once
 * sitios de render, en 10 archivos** (medido, ver el encabezado), de golpe y sin
 * que ningún criterio lo pida. Migrar esas dos es otra etapa.
 *
 * ⇒ el selector aparece **sólo si llega `onTamanoDePagina`**. Un solo prop
 * gobierna el opt-in: no hay forma de pedirlo a medias ni de elegir otra escala,
 * que es lo que `F1-CA-57` y `F5-CA-11` fijan.
 *
 * ⛔ **Y la vuelta a la página 1 la hace la primitiva.** `PRD_R3.md:721`: *«Cambiar
 * las filas por página recalcula las páginas y vuelve a la página 1, con el filtro
 * aplicado.»* Dejársela a cada pantalla es cómo nacen las dos versiones del mismo
 * `setPagina(1)` —`auditoria.tsx:485` ya tiene la suya— y la que se olvide va a
 * mostrar otras filas sin avisar, en la página 7 de una escalera que ya no existe.
 *

 * ⚠️ **EL BLANCO DE TOQUE, MEDIDO Y NO DEDUCIDO.** `estilos.css` le pone
 * `min-height: 32px` a todo `input` en la base, y **44px** a
 * `input:not([type="checkbox"]):not([type="radio"])` — pero esa segunda regla vive
 * DENTRO de `@media (max-width: 640px)`, no en la base. ⇒ el campo hace crecer la
 * barra a 32px en escritorio y a 44px en teléfono, en ~~las ocho pantallas~~ **los
 * once sitios de render** a la vez [`R3-UI0` 25/9: el número, medido].
 * **Eso se deja pasar a propósito**: bajarle la altura al campo para que la barra
 * quede fina rompería el blanco de toque, que es la razón por la que esa regla
 * existe. La barra crece; el pulgar sigue llegando.
 *
 * ⛔ **`type="text"` con `inputMode="numeric"`, no `type="number"`.** El control
 * nativo de número trae flechas de incremento que en la barra son dos blancos de
 * toque de 8px de alto, cambia de forma entre navegadores —lo mismo que
 * `estilos.css` ya tuvo que corregir a mano en los trece `<select>`— y acepta
 * `e`, `+` y `-` como texto válido. La validación es de esta unidad de todos modos:
 * lo único que aportaría el tipo nativo es una forma que no controlamos.
 */

import { useId, useState } from "react"
import type { JSX, ReactNode } from "react"

/** El nombre de lo que se está paginando, para el conteo. */
export type UnidadPaginada = {
  readonly singular: string
  readonly plural: string
}

/**
 * ⭐ `R3-UI0` · **LA ESCALA DE R3, UNA VEZ.** `PRD_R3.md:721` y regla 27 (`:880`):
 * *«“Filas por página” ofrece 10, 50, 100, 200 y 500, con 10 al abrir»*.
 *
 * ⛔ Es una constante exportada y no un literal en cada pantalla: `R3-F1`
 * (Conversaciones) y `R3-F5` (Participantes) piden **la misma**, y dos listas que
 * tienen que coincidir escritas en dos lugares divergen (`ADR-0231`).
 */
export const FILAS_POR_PAGINA = [10, 50, 100, 200, 500] as const

/**
 * ⭐ Con cuántas filas ABRE una pantalla de R3. `PRD_R3.md:721`: *«al abrir la
 * pantalla, 10»*, y *«al recargar … 10 filas por página»*.
 *
 * ⚠️ **La primitiva no puede imponerlo, y va dicho:** el `tamanoDePagina` es
 * estado de la pantalla —es lo que ésta le manda al borde—, así que el valor de
 * arranque lo elige quien tiene ese estado. Lo que esta constante hace es que no
 * haya dos pantallas de R3 eligiendo dos arranques: se importa, no se escribe.
 */
export const FILAS_POR_PAGINA_AL_ABRIR = FILAS_POR_PAGINA[0]

export function Paginado(props: {
  /**
   * Qué se pagina, en genitivo, para el `aria-label` del `<nav>`: «Paginación de
   * {etiqueta}». Es lo único que distingue a una barra de otra para quien navega
   * por puntos de referencia, y hay pantallas con DOS barras (`metricas`).
   */
  readonly etiqueta: string
  readonly pagina: number
  /** El total EN LA BASE que devolvió el borde. Nunca `items.length`. */
  readonly total: number
  readonly tamanoDePagina: number
  readonly onPagina: (pagina: number) => void
  /** Si va, el conteo dice el nombre de la cosa además del número. */
  readonly unidad?: UnidadPaginada
  /**
   * Controles propios de una pantalla, a la izquierda de «Anterior».
   *
   * ⭐ Existe por `envios/formulario.tsx`, que es **el que hoy está afuera** y
   * tiene un selector de «Filas por página» que ninguno de los otros siete tiene.
   * La alternativa era dejarlo afuera «porque es distinto», que no arregla el
   * defecto: lo confirma. La primitiva lo aloja, y el lugar donde se aloja está
   * declarado en vez de improvisado por cada pantalla.
   */
  readonly children?: ReactNode
  /**
   * ⭐ `R3-UI0` · **EL OPT-IN DE «Filas por página».** Si llega, la barra dibuja
   * el selector con `FILAS_POR_PAGINA` a la izquierda de todo; si no llega, la
   * barra es exactamente la de antes.
   *
   * ⛔ Un solo prop y no un par `{ opciones, onCambio }`: la escala de R3 es la
   * misma para las dos pantallas que la piden (`F1-CA-57`, `F5-CA-11`), así que
   * ofrecer un punto donde elegir otra es ofrecer la divergencia que esta
   * primitiva existe para cerrar. Las dos pantallas con escala propia
   * (`auditoria.tsx`, `envios/formulario.tsx`) siguen usando `children`.
   *
   * ⚠️ La primitiva llama TAMBIÉN a `onPagina(1)` cuando la página no era la 1,
   * porque `PRD_R3.md:721` lo pide junto con el cambio de tamaño. Quien reciba
   * los dos avisos en el mismo turno de React los ve batcheados.
   */
  readonly onTamanoDePagina?: (tamanoDePagina: number) => void
  /**
   * ⭐⭐ `R3-UI0` `[25/9/2026]` · **EL QUINTO ELEMENTO, DICHO Y NO DERIVADO.**
   *
   * Si llega, manda: `true` ⇒ «Ir a la página» está SIEMPRE (también con una
   * página y con `total` 0, `PRD_R3.md:682`/`:721`/`:723`); `false` ⇒ vale la regla
   * pre-R3 (`paginas > 1`). **Si no llega, el default es `onTamanoDePagina !==
   * undefined`**, o sea la decisión del operador tal cual: la barra de R3 lo trae y
   * ninguna otra.
   *
   * ⛔⛔ **POR QUÉ EXISTE ESTE PROP Y NO ALCANZA CON DERIVARLO.** El eje no es
   * *«pide “Filas por página”»* sino *«pide “Filas por página” **por el prop**»*, y
   * eso es una distinción que **dos pantallas de hoy ya rompen**: Auditoría
   * (`configuracion-del-chatbot/auditoria.tsx:450`) y Nuevo envío
   * (`envios/formulario.tsx:786`) dibujan «Filas por página» por `children`, con
   * escala propia. El `acceptance` de `R3-UI0` dice que **migrarlas es otra etapa**
   * ⇒ el día que se migren al prop, con la derivación sola **heredaban «Ir a la
   * página» en silencio** y `R1-F3-28` (`aw47` §5.12) y `CA-88` (`aw48:1587`)
   * volvían a rojo.
   *
   * ⇒ ese día la migración tiene **una salida declarada**: `conIrALaPagina={false}`
   * junto con `onTamanoDePagina`. `pruebas/aw31-paginado.test.tsx` mide los tres
   * estados —barra pre-R3, barra de R3, y barra de R3 con la salida— así que el
   * invariante **no vive en un comentario**: si alguien lo cambia, hay un rojo con
   * el nombre de los dos criterios adentro.
   *
   * ⚠️ Y al revés también: una pantalla que quiera el campo escribible **sin** el
   * selector de tamaño pasa `conIrALaPagina` a secas. Ninguna lo hace hoy.
   */
  readonly conIrALaPagina?: boolean
  /**
   * ⭐ `R4-UI0` · **LA ESCALA OPCIONAL, Y EL PUNTO DE MILES QUE VIENE CON ELLA.**
   *
   * Si llega una lista, la barra la usa para «Filas por página» y pinta TODOS
   * sus números —las filas y el total— con punto de miles; si no llega, la barra
   * es exactamente la de hoy, con `FILAS_POR_PAGINA` de R3.
   *
   * ⛔ Es un prop y NO un cambio a `FILAS_POR_PAGINA`: agregar 1.000 a la
   * constante de R3 «porque es más prolijo» cambia de golpe Conversaciones,
   * Participantes y Auditoría, que ningún CA de R4 pide cambiar, y enciende
   * `R3-UI1.gate.ts:1225-1240` y `R3-UI6.gate.ts:161`.
   */
  readonly escala?: readonly number[]
  /**
   * ⭐ 3/11/2026 · **LA BARRA QUE VIVE DENTRO DE OTRO `<form>`.** Si llega `true`,
   * «Ir a la página» NO es un `<form>` propio: un `<form>` dentro de otro es HTML
   * inválido (React lo avisa en desarrollo, y el humo de la consola lo cuenta como
   * pantalla rota). Mismo campo, mismo botón «Ir» y mismo Enter; lo que cambia es
   * quién atiende el Enter —el campo, que además lo CORTA para que el navegador no
   * haga el envío implícito del formulario de afuera— y que «Ir» es `type="button"`.
   *
   * ⛔ Es opt-in y no el comportamiento de todas: el docblock ② sigue valiendo para
   * las barras sueltas. Hoy lo pasa un solo sitio: `envios/formulario.tsx` (Nuevo envío).
   */
  readonly dentroDeUnFormulario?: boolean
}): JSX.Element {
  const { pagina, total, tamanoDePagina, onPagina, unidad, onTamanoDePagina, escala } = props

  const paginas = total > 0 ? Math.ceil(total / tamanoDePagina) : 1

  /*
   * ⭐ `R4-UI0` · la escala de la pantalla manda; sin ella, la lista de R3. Y el
   * punto de miles es OPT-IN junto con la escala: sin el prop nuevo los números
   * salen crudos, como hoy, así que la barra de R3 no cambia.
   */
  const opcionesDeFilas: readonly number[] = escala ?? FILAS_POR_PAGINA
  const formatearNumero = (valor: number): string =>
    escala === undefined ? String(valor) : valor.toLocaleString("es-CL")

  /*
   * ⭐ `R3-UI0` · **«ESTA ES LA BARRA DE R3».** Lo dice el opt-in de «Filas por
   * página», que es el otro elemento que `PRD_R3.md:682` agrega a la lista: las dos
   * pantallas de R3 que piden uno piden el otro. Ver el docblock ③ de este archivo:
   * de esta constante depende la `PARADA` de «Ir a la página».
   *
   * ⛔ **El predicado se escribe UNA vez y se lee de acá**, incluida la condición
   * del selector de abajo. Hasta el 25/9/2026 estaba escrito **dos** veces —acá por
   * nombre y en el `{…&& (` del selector por el literal `onTamanoDePagina !==
   * undefined`—, y su acoplamiento con `conIrALaPagina` es la mitad de la `PARADA`
   * del docblock ③: dos escrituras se separan en silencio.
   */
  const barraDeR3 = onTamanoDePagina !== undefined

  /*
   * ⭐ `R3-UI0` `[25/9/2026]` · el quinto elemento: **el prop manda, la barra de R3
   * es el default.** Ver §`conIrALaPagina` arriba y el docblock ③.
   */
  const conIrALaPagina = props.conIrALaPagina ?? barraDeR3

  const idCampo = useId()
  const idAviso = `${idCampo}-aviso`

  const [escrito, setEscrito] = useState("")
  const [aviso, setAviso] = useState<string | null>(null)

  const ir = (evento: { preventDefault(): void }): void => {
    evento.preventDefault()
    const texto = escrito.trim()

    if (texto === "") {
      // `R1.1-F5-CA-85` · ~~«Escribí»~~ → «Escribe» (`PRD_R1.1.md:904`: tú, sin voseo).
      // ⚠️ Este aviso vive en el paginado COMPARTIDO, o sea en casi toda pantalla
      // nueva, y aun así el recorrido nunca lo vio: sólo existe tras mandar el
      // salto de página vacío.
      setAviso("Escribe un número de página.")
      return
    }
    if (!/^\d+$/.test(texto)) {
      setAviso(`«${texto}» no es un número de página.`)
      return
    }

    const destino = Number(texto)
    if (destino < 1 || destino > paginas) {
      // ⛔ No navega y no corrige: dice el rango y deja lo escrito donde está.
      setAviso(`No hay página ${destino}: esta lista tiene ${paginas}.`)
      return
    }

    setAviso(null)
    setEscrito("")
    onPagina(destino)
  }

  const campoIr = (
    <input
      id={idCampo}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={escrito}
      onChange={(evento) => {
        setEscrito(evento.target.value)
        // El aviso describe lo ANTERIOR; en cuanto se corrige, sobra.
        if (aviso !== null) setAviso(null)
      }}
      aria-invalid={aviso !== null}
      aria-describedby={aviso === null ? undefined : idAviso}
      // Sin `<form>` propio (`dentroDeUnFormulario`), el Enter lo atiende el campo y
      // `ir` lo corta: si no, el navegador enviaría el formulario de AFUERA.
      onKeyDown={
        props.dentroDeUnFormulario === true
          ? (evento) => {
              if (evento.key === "Enter") ir(evento)
            }
          : undefined
      }
    />
  )

  return (
    <nav className="paginado" aria-label={`Paginación de ${props.etiqueta}`}>
      <div className="paginado__barra">
        {barraDeR3 && (
          <label className="paginado__filas">
            Filas por página
            {/*
              ⛔⛔ **`aria-label` CON EL MISMO TEXTO QUE SE VE, y no es
              redundante.** Lo dejó medido `gates/pantalla-auditoria.spec.ts` sobre
              la copia de `configuracion-del-chatbot/auditoria.tsx:453`: con un
              `<label>` ENVOLVENTE el nombre accesible se computa sobre el
              `textContent` del label, **que incluye el valor del `<select>`** ⇒ el
              nombre real sería «Filas por página 10» y cambiaría con lo que el
              operador elija. Un juez que busque el control cuyo rótulo es
              exactamente «Filas por página» no lo encuentra.

              ⚠️ La copia de `envios/formulario.tsx` tiene el MISMO defecto y no se
              toca acá: su escala es propia y migrarla es otra etapa.
            */}
            <select
              aria-label="Filas por página"
              value={tamanoDePagina}
              onChange={(evento) => {
                const nuevo = Number(evento.target.value)
                /*
                 * ⛔ El orden importa poco para React pero mucho para leerlo: se
                 * pide el tamaño nuevo y, si hacía falta, la vuelta a la 1. La
                 * guarda evita despertar el refetch de una pantalla que ya estaba
                 * en la página 1.
                 */
                onTamanoDePagina(nuevo)
                if (pagina !== 1) onPagina(1)
              }}
            >
              {opcionesDeFilas.map((filas) => (
                <option key={filas} value={filas}>
                  {formatearNumero(filas)}
                </option>
              ))}
            </select>
          </label>
        )}

        {props.children}

        <button type="button" onClick={() => onPagina(pagina - 1)} disabled={pagina <= 1}>
          Anterior
        </button>

        <span aria-live="polite">
          Página {formatearNumero(pagina)} de {formatearNumero(paginas)} · {formatearNumero(total)}
          {unidad === undefined ? "" : ` ${total === 1 ? unidad.singular : unidad.plural}`}
        </span>

        <button type="button" onClick={() => onPagina(pagina + 1)} disabled={pagina >= paginas}>
          Siguiente
        </button>

        {/*
          ⛔⛔ `R3-UI0` · **LA CONDICIÓN DE UNA LÍNEA DE LA `PARADA` DEL DOCBLOCK ③.**

          `conIrALaPagina || paginas > 1`, con `conIrALaPagina = props.conIrALaPagina
          ?? barraDeR3`:
            · en la barra de R3 (`onTamanoDePagina` presente) el campo está
              SIEMPRE — `PRD_R3.md:682`, también con una página y con `total` 0;
            · en los otros **nueve** sitios de render pre-R3 la barra queda
              **idéntica** a antes, que es lo que mantiene verdes `R1-F3-28`
              (`aw47` §5.12, Banco de contenido) y `CA-88` (`aw48:1587`, pestaña
              Auditoría).

          ⛔ Cambiar esto a `true` es alcance nuevo sobre dos criterios de R1 y R2
          que hoy están verdes, y su medición está en el docblock ③. No se toca sin
          esa decisión. Lo que SÍ se puede sin decidir nada es pasar
          `conIrALaPagina={false}` en la pantalla que se migre al prop del selector.
        */}
        {(conIrALaPagina || paginas > 1) &&
          (props.dentroDeUnFormulario === true ? (
            <div className="paginado__ir">
              <label htmlFor={idCampo}>Ir a la página</label>
              {campoIr}
              <button type="button" onClick={ir}>
                Ir
              </button>
            </div>
          ) : (
            <form className="paginado__ir" onSubmit={ir}>
              <label htmlFor={idCampo}>Ir a la página</label>
              {campoIr}
              <button type="submit">Ir</button>
            </form>
          ))}
      </div>

      {aviso !== null && (
        <p className="paginado__aviso" id={idAviso} role="alert">
          {aviso}
        </p>
      )}
    </nav>
  )
}
